/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-shadow */
import dayjs from 'dayjs'
import {
  fromPairs,
  isArray,
  isDate,
  isEqual,
  isFunction,
  isNull,
  isObject,
  isString,
  isUndefined,
  keyBy,
  omit,
  toPairs,
  toString,
  transform,
} from 'lodash'

export const parser = {
  value: {},
  is(object: { [key: string]: any }) {
    this.value = object
    return this
  },
  fieldParser(
    parsers: Array<{ fields: Array<string>; callback: (value: any) => any }>,
  ) {
    this.value = fromPairs(
      toPairs(this.value).map(([key, value]) => {
        let processor = parsers.find(p => p.fields.find(f => f === key))
        processor ??= parsers.find(p => p.fields.find(f => f === '*'))
        if (processor?.callback && isFunction(processor?.callback)) {
          return [key, processor?.callback(value)]
        }
        return [key, value]
      }),
    )
    return this
  },
  updatedFields(origObj: { [key: string]: any }) {
    function changes(newObj: any, origObj: any) {
      return transform(newObj, (result: any, value: any, key: any) => {
        if (!isEqual(value, origObj[key])) {
          result[key] =
            isObject(value) &&
            isObject(origObj[key]) &&
            !(isDate(value) || isArray(value)) // becouse date is an object but no need for recursion for it. and we take the whole array not the difference.
              ? changes(value, origObj[key])
              : value
        }
      })
    }
    this.value = changes(this.value, origObj)
    return this
  },
  deepEmptyStringSanitization(keys?: Array<string>) {
    function emptyStringFromObj(obj: object) {
      return transform(obj, (result: Array<any>, value, key) => {
        if (
          !(
            isString(value) &&
            value === '' &&
            (!keys || (keys && keys.includes(key)))
          )
        )
          result[key] =
            !isDate(value) && isObject(value)
              ? emptyStringFromObj(value)
              : value
      })
    }
    this.value = emptyStringFromObj(this.value)
    return this
  },
  filterSanitization() {
    function sanitize(obj: object) {
      return transform(obj, (result: Array<any>, value, key) => {
        if (
          !(
            isNull(value) ||
            isUndefined(value) ||
            (isArray(value) && (value as Array<any>).length === 0) ||
            (isString(value) && value === '')
          )
        )
          result[key] =
            !isDate(value) && isObject(value) ? sanitize(value) : value
      })
    }
    this.value = sanitize(sanitize(this.value)) // first time removes null values from array, second time it removes the empty array
    return this
  },
  deepNullToEmptyString() {
    function nullFromObj(obj: object) {
      return transform(obj, (result: Array<any>, value, key) => {
        if (value == null) {
          result[key] = ''
        } else result[key] = isObject(value) ? nullFromObj(value) : value
      })
    }
    this.value = nullFromObj(this.value)
    return this
  },
  deepOmitNull() {
    function nullFromObj(obj: object) {
      return transform(obj, (result: Array<any>, value, key) => {
        if (value != null)
          result[key] =
            isObject(value) && !isDate(value) ? nullFromObj(value) : value
      })
    }
    this.value = nullFromObj(this.value)
    return this
  },
  deepParseDate() {
    function changes(obj: { [key: string]: any }) {
      return transform(
        obj,
        (result: { [key: string]: any }, value: any, key: string) => {
          if (
            !isDate(value) &&
            dayjs(value).isValid() &&
            dayjs(value).isAfter(dayjs('2022-1-1'))
          ) {
            result[key] = new Date(value)
            return
          }
          result[key] = value
        },
      )
    }
    this.value = changes(this.value)
    return this
  },
  deepKeyOmit(keys: string | Array<string>) {
    const keysToOmitIndex = keyBy(isArray(keys) ? keys : [keys])

    function omitFromObject(obj: object) {
      return transform(obj, (result: Array<any>, value, key) => {
        if (key in keysToOmitIndex) {
          return
        }
        result[key] = isObject(value) ? omitFromObject(value) : value
      })
    }
    this.value = omitFromObject(this.value)
    return this
  },
  deepValueOmit(values: string | Array<string>) {
    const valuesToOmitIndex = keyBy(isArray(values) ? values : [values])

    function omitFromObject(obj: object) {
      return transform(obj, (result: Array<any>, value, key) => {
        if (value in valuesToOmitIndex) {
          return
        }
        result[key] = isObject(value) ? omitFromObject(value) : value
      })
    }
    this.value = omitFromObject(this.value)
    return this
  },
  deepIdToString() {
    function idFromObject(obj: object) {
      return transform(obj, (result: Array<any>, value, key) => {
        if (key === 'id' && !isString(value)) {
          result[key] = toString(value)
        } else result[key] = isObject(value) ? idFromObject(value) : value
      })
    }
    this.value = idFromObject(this.value)
    return this
  },
  omit(keys: string | Array<string>) {
    this.value = omit(this.value, keys)
    return this
  },
}

export const getHoursMinute = (date: Date) => ({
  hours: dayjs(date).get('hour'),
  minutes: dayjs(date).get('minute'),
})

export const setHoursMins = ({
  date,
  hours,
  minute,
}: {
  date: Date
  hours: number
  minute: number
}) => dayjs(date).set('hour', hours).set('minute', minute).toDate()
